import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { buildHTMLText, useCopyPaste } from "@core/composables/useCopyPaste";
import { useUndo } from "@editor/store/undo/undo.store";

import { getIncidentCopyData } from "@editor/items/incident/incidentFunctions";
import { getActionCopyData } from "@editor/items/action/ActionFunctions";
import { getScenarioCopyData } from "@editor/items/scenario/scenarioFunctions";
import { getIncidentTriggerCopyData } from "@editor/items/incidentTrigger/triggerFunctions";
import { getRequirementCopyData } from "@editor/items/requirement/requirementFunctions";

import { useModelStore } from "@editor/items/model/model.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useConditionStore } from "@editor/items/condition/conditions.store";
import { useStakeholderStore } from "@editor/items/stakeholder/stakeholders.store";
import { useScenarioEndStore } from "@editor/items/scenarioEnd/scenarioEnds.store";
import { useVariablesViewStore } from "./variablesView.store";
import { useIncidentTriggerStore } from "@editor/items/incidentTrigger/incidentTriggers.store";
import { useRequirementStore } from "@editor/items/requirement/requirements.store";

import type { ItemType } from "@editor/items/ItemOfStore";
import type { IncidentItem } from "@editor/items/incident/IncidentItem";
import type { ActionItem } from "@editor/items/action/ActionItem";
import type { ScenarioItem } from "@editor/items/scenario/ScenarioItem";
import type { EffectItem } from "@editor/items/effect/EffectItem";
import type { IncidentTriggerItem } from "@editor/items/incidentTrigger/IncidentTriggerItem";
import type { ConditionItem } from "@editor/items/condition/ConditionItem";
import type { ScenarioEndItem } from "@editor/items/scenarioEnd/ScenarioEndItem";
import type { StakeholderItem } from "@editor/items/stakeholder/StakeholderItem";
import type { RequirementItem } from "@editor/items/requirement/RequirementItem";
import type { ActionCostCopyItem } from "@editor/items/action/ActionCostItem";

export function useVariablesCopyPaste() {
  const undoStore = useUndo();
  const modelStore = useModelStore();

  const incidentStore = useIncidentStore();
  const actionStore = useActionStore();
  const scenarioStore = useScenarioStore();
  const variablesView = useVariablesViewStore();
  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const effectStore = useEffectStore();

  const { copy, onPasteEvent, onCopyEvent, getClipboardItems } = useCopyPaste();
  const { t } = useI18n();
  const toast = useToast();

  function getCopyData(
    items: (
      | IncidentItem
      | ActionItem
      | ScenarioItem
      | EffectItem
      | IncidentTriggerItem
      | ScenarioItem
      | ConditionItem
      | ScenarioEndItem
      | StakeholderItem
      | RequirementItem
      | ActionCostCopyItem
    )[]
  ) {
    const data = [];
    let html = "";

    for (const item of items) {
      switch (item.type) {
        case "incident": {
          data.push(...getIncidentCopyData(item));
          const htmlBuilder = buildHTMLText(item)
            .addTitle(item.title)
            .addHTMLText(item.description);

          const effects = effectStore.getByActivator(item);
          if (effects.length > 0) {
            htmlBuilder.addTitle(t("objectNames.effect", 4), 3);

            htmlBuilder.addList(
              effects.map((effect) => {
                const affectedItem = modelStore.getItem(
                  effect.affectedType,
                  effect.affectedId
                );
                return `${affectedItem?.title}: ${effect.impulseExpressions.join(
                  ", "
                )}`;
              })
            );
          }

          html += htmlBuilder.toString();
          break;
        }

        case "action": {
          data.push(...getActionCopyData(item));
          const htmlBuilder = buildHTMLText(item)
            .addTitle(item.title)
            .addHTMLText(item.description);
          
          const costs = item.costs;
          if (costs && costs.length > 0) {
            htmlBuilder.addTitle(t("labels.costs", 4), 3);

            htmlBuilder.addList(
              costs.map((cost) => {
                const element = modelStore.getItem(cost.elementUid);
                return `${element?.title}: ${cost.expression}`;
              })
            );
          }

          const effects = effectStore.getByActivator(item);
          if (effects.length > 0) {
            htmlBuilder.addTitle(t("objectNames.effect", 4), 3);

            htmlBuilder.addList(
              effects.map((effect) => {
                const affectedItem = modelStore.getItem(
                  effect.affectedType,
                  effect.affectedId
                );
                return `${affectedItem?.title}: ${effect.impulseExpressions.join(
                  ", "
                )}`;
              })
            );
          }

          html += htmlBuilder.toString();
          break;
        }

        case "scenario": {
          data.push(...getScenarioCopyData(item));
          html += buildHTMLText(item)
            .addTitle(item.title)
            .addHTMLText(item.description)
            .toString();
          break;
        }

        case "effect": {
          data.push(item);
          const affectedItem = modelStore.getItem(
            item.affectedType,
            item.affectedId
          );
          html += buildHTMLText(item)
            .addText(
              `${affectedItem?.title}: ${item.impulseExpressions.join(", ")}`
            )
            .toString();
          break;
        }

        case "incidentTrigger": {
          data.push(...getIncidentTriggerCopyData(item));
          html += buildHTMLText(item)
            .addTitle(item.title)
            .addHTMLText(item.description)
            .toString();
          break;
        }
        
        case "requirement":
          data.push(...getRequirementCopyData(item));
          html += buildHTMLText(item)
            .addTitle(item.title)
            .addHTMLText(item.description)
            .toString();
          break;

        case "condition": {
          data.push(item);
          break;
        }

        case "stakeholder":
          data.push(item);
          html += buildHTMLText(item)
            .addTitle(item.title)
            .addHTMLText(item.description)
            .toString();
          break;
        
        case "actionCost":
          data.push(item);
          html += buildHTMLText(item)
            .addText(item.expression)
            .toString();
          break;

        default:
          console.warn(`Unknown item type ${item.type}`);
      }
    }

    return {
      data,
      html
    };
  }
  function copyItem(
    item:
      | IncidentItem
      | ActionItem
      | ScenarioItem
      | EffectItem
      | IncidentTriggerItem
      | ScenarioItem
      | ConditionItem
      | ScenarioEndItem
      | StakeholderItem
      | RequirementItem
      | ActionCostCopyItem
  ) {
    const { data, html } = getCopyData([item]);

    if (data.length === 0) return;

    // Notify user via toast that item has been copied
    const message = t("messages.xCopiedToClipboard", {
      title:
        "title" in item && item.title
          ? `${t("quote.open")}${item.title}${t("quote.close")}`
          : t(`objectNames.${item.type}`)
    });
    toast.success(message, { timeout: 2500 });

    copy({
      data,
      html
    });
  }

  function copyItems(
    selectedItems: (
      | IncidentItem
      | ActionItem
      | ScenarioItem
      | EffectItem
      | IncidentTriggerItem
      | ConditionItem
      | ScenarioEndItem
      | StakeholderItem
      | RequirementItem
      | ActionCostCopyItem
    )[]
  ) {
    if (selectedItems.length === 0) {
      console.warn("no item selected for copying");
      return;
    }
    if (selectedItems.length === 1) {
      return copyItem(selectedItems[0]);
    }

    const itemsData = getCopyData(selectedItems);
    copy(itemsData);

    // Notify user via toast that items have been copied
    showCopyMessage(selectedItems);
  }

  function registerPasteEvent() {
    /**
     * Handle CTRL+V
     */
    onPasteEvent(pasteClipboardItems);

    onCopyEvent(() => {
      const mainItems = variablesView.mainSelectedItems.map((item) => getItem(item));
      const subItems = variablesView.subSelectedItems.map((item) => getItem(item));
      const subSubItems = variablesView.subSubSelectedItems.map((item) => getItem(item));

      if (mainItems.length === 0 && subItems.length === 0 && subSubItems.length === 0) {
        console.warn("no item selected for copying");
        return;
      }

      if (subSubItems.length > 0) {
        const items = subSubItems.filter((i): i is ConditionItem => i != null && i.type === "condition");
        showCopyMessage(items);
        return getCopyData(items);
      }
      if (subItems.length > 0) {
        const items = subItems.filter((i): i is (EffectItem | StakeholderItem | ScenarioEndItem | IncidentTriggerItem | RequirementItem) => i != null && (i.type === "effect" || i.type === "stakeholder" || i.type === "scenarioEnd" || i.type === "incidentTrigger" || i.type === "requirement"));
        showCopyMessage(items);
        return getCopyData(items);
      }
      if (mainItems.length > 0) {
        const items = mainItems.filter((i): i is (IncidentItem | ActionItem | ScenarioItem) => i != null && (i.type === "incident" || i.type === "action" || i.type === "scenario"));
        showCopyMessage(items);
        return getCopyData(items);
      }
    });
  }

  function showCopyMessage(items: { type: string; title?: string }[]) {
    if (items.length === 0) return;

    if (items.length === 1) {
      const item = items[0];
      const message = t("messages.xCopiedToClipboard", {
        title:
          item.title
            ? `${t("quote.open")}${item.title}${t("quote.close")}`
            : t(`objectNames.${item.type}`)
      });
      toast.success(message, { timeout: 2500 });
      return;
    }

    const type = items.reduce((prev, curr) => {
      if (prev === curr.type) {
        return prev;
      }
      return "object";
    }, items[0].type);

    const message = t("messages.xCopiedToClipboard", {
      title: items.length + " " + t(`objectNames.${type}`, items.length),
    });
    toast.success(message, { timeout: 2500 });
  }

  async function pasteClipboardItems(items: any[]) {
    const mainItem = getItem(variablesView.mainSelection);
    const subItem = getItem(variablesView.subSelection);
    // const subSubItem = getItem(variablesView.subSubSelection);

    undoStore.startGroup("model", "paste");

    const newItemUids = new Map<string, string>();

    const images = items.filter((item) => item.type == "image");
    const mainItems = items.filter(
      (item) =>
        item.type == "incident" ||
        item.type == "action" ||
        item.type == "measure" ||
        item.type == "scenario" ||
        item.type == "actionCost"
    );
    const subItems = items.filter(
      (item) =>
        item.type == "effect" ||
        item.type == "stakeholder" ||
        item.type == "scenarioEnd" ||
        item.type == "incidentTrigger" ||
        item.type == "requirement"
    );
    const subSubItems = items.filter((item) => item.type == "condition");

    function getNewId(type: string, id: number | null | undefined) {
      if (id == null) return null;
      const uid = `${type}_${id}`;
      const newUid = newItemUids.get(uid);
      if (newUid == null) return null;
      return parseInt(newUid.split("_")[1]);
    }

    async function importLinkedImage<T extends string>(
      property: T,
      item: { [k in T]: number | null }
    ) {
      if (item[property] == null) return;
      const image = images.find((i) => i.id === item[property]);
      if (image != null) {
        const imageStore = useImageStore();
        // check if there's already an image with the same mediaUUID
        const mediaUUIDExists = imageStore.linkedMediaUUIDs.has(image.mediaUUID);
        if (mediaUUIDExists) {
          console.log("media uuid exists, reusing media item");
          image.data = null;
        }
        const newImage = await imageStore.importItem(image);
        item[property] = newImage.id;
      } else {
        item[property] = null;
      }
    }

    for (const item of mainItems) {
      if (item.type === "incident") {
        await importLinkedImage("imageId", item);

        const incident = await incidentStore.importItem(item);
        newItemUids.set(item.uid, incident.uid);
        variablesView.selectMain(incident);
      }
      if (item.type === "action" || item.type === "measure") {
        item.type = "action";
        
        await importLinkedImage("imageId", item);

        // clean up costs
        for (const _resourceId in item.costs) {
          // remove costs that are not in the resource store
          const resourceId = parseInt(_resourceId);
          if (resourceStore.items[resourceId] == null) {
            delete item.costs[resourceId];
          }
          // remove empty costs
          if (item.costs[resourceId] == null) {
            delete item.costs[resourceId];
          }
        }
        
        const action = await actionStore.importItem(item);
        newItemUids.set(item.uid, action.uid);
        variablesView.selectMain(action);
      }
      if (item.type === "scenario") {
        await importLinkedImage("startImageId", item);
        await importLinkedImage("endImageId", item);

        const scenario = await scenarioStore.importItem(item);
        newItemUids.set(item.uid, scenario.uid);
        variablesView.selectMain(scenario);
      }
      if (item.type === "actionCost") {
        if (mainItem?.type != "action") {
          console.warn("cannot import actionCost without action");
          continue;
        }
        const action = mainItem as ActionItem;
        const costs = [...action.costs];
        const costIndex = costs.findIndex(
          (cost) => cost?.elementUid === item.elementUid
        );
        delete item.type; // costs do not have a type, this is just for the copy function
        if (costIndex >= 0) {
          costs[costIndex] = item;
        } else {
          costs.push(item);
        }
        actionStore.update({
          id: action.id,
          costs
        });
      }
    }

    for (const item of subItems) {
      if (item.type === "scenarioEnd") {
        // connect to scenario
        item.scenarioId = getNewId("scenario", item.scenarioId);

        const scenarioEnd = await useScenarioEndStore().importItem(item);
        newItemUids.set(item.uid, scenarioEnd.uid);
      }
      if (item.type === "effect") {
        // connect to action/incident
        item.activatorId = getNewId(item.activatorType, item.activatorId);

        // DO not import an effect affecting an non-existing item
        if (!doesItemExist(item.affectedType, item.affectedId)) {
          console.warn(
            `${item.affectedType} ${item.affectedId} does not exist`
          );
          continue;
        }

        const effectStore = useEffectStore();

        // If the activator was not copied with the item,
        // try to associate it with the current item
        if (item.activatorId == null) {
          if (mainItem?.type == "action" || mainItem?.type == "incident") {
            item.activatorId = mainItem.id;
            item.activatorType = mainItem.type;

            // check if there is already an effect for this activator
            // affecting the same item
            const activatorEffects = effectStore.getByActivator(mainItem);
            const existingEffect = activatorEffects.find(
              (e) =>
                e.affectedId == item.affectedId &&
                e.affectedType == item.affectedType
            );
            if (existingEffect != null) {
              // OVERWRITE the existing effect
              await effectStore.delete(existingEffect);
            }
          } else {
            // Do not import this unassociated effect.
            continue;
          }
        }

        const effect = await effectStore.importItem(item);
        newItemUids.set(item.uid, effect.uid);
      }
      if (item.type === "stakeholder") {
        item.targetIds = (item.targetIds || item.elementIds || []).filter(
          (id: number) => id in targetStore.items
        );
        item.resourceIds = (item.resourceIds || []).filter(
          (id: number) => id in resourceStore.items
        );
        item.actionIds = (item.actionIds || []).filter(
          (id: number) => id in actionStore.items
        );

        // If we ONLY insert the stakeholder, try to associate it with the current item
        if (items.length == 1) {
          if (
            mainItem?.type == "action" &&
            !item.actionIds.includes(mainItem.id)
          ) {
            item.actionIds.push(mainItem.id);
          } else {
            // Do not import this unassociated stakeholder.
            console.warn("do not import this unassociated stakeholder");
            continue;
          }
        }

        const stakeholder = await useStakeholderStore().importItem(item);
        newItemUids.set(item.uid, stakeholder.uid);
      }
      if (item.type === "incidentTrigger") {
        // connect to incident
        item.incidentId = getNewId("incident", item.incidentId);

        // If the incident was not copied with the item,
        // try to associate it with the current item
        if (item.incidentId == null) {
          if (mainItem?.type == "incident") {
            item.incidentId = mainItem.id;
          } else {
            // Do not import this unassociated incidentTrigger.
            continue;
          }
        }

        const incidentTriggerStore = useIncidentTriggerStore();
        const trigger = await incidentTriggerStore.importItem(item);
        newItemUids.set(item.uid, trigger.uid);
      }
      if (item.type == "requirement") {
        item.itemId = getNewId(item.itemType, item.itemId);

        // If the item was not copied with the requirement,
        // try to associate it with the current item
        if (item.itemId == null) {
          if (mainItem?.type == "action") {
            item.itemId = mainItem.id;
            item.itemType = mainItem.type;
          } else {
            // Do not import this unassociated requirement.
            continue;
          }
        }

        const requirementStore = useRequirementStore();
        const requirement = await requirementStore.importItem(item);
        newItemUids.set(item.uid, requirement.uid);
      }
    }

    for (const item of subSubItems) {
      if (item.type === "condition") {
        item.srcId = getNewId(item.srcType, item.srcId);

        // If the incident was not copied with the item,
        // try to associate it with the current item
        if (item.srcId == null) {
          if (subItem?.type == "incidentTrigger") {
            item.srcId = subItem.id;
            item.srcType = subItem.type;
          } else if (subItem?.type == "requirement") {
            item.srcId = subItem.id;
            item.srcType = subItem.type;
          } else {
            // Do not import this unassociated incidentTrigger.
            continue;
          }
        }

        // make sure the activator is still valid
        if (item.activatorType != "round" && item.activatorType != "roundRange") {
          resetIfInvalidItem(item, "activatorType", "activatorId");
        }

        // make sure the comparedItem is still valid
        if (item.comparedType != "round" && item.comparedType != "roundRange") {
          resetIfInvalidItem(item, "comparedType", "comparedId");
        }

        const condition = await useConditionStore().importItem(item);
        newItemUids.set(item.uid, condition.uid);
      }
    }

    undoStore.endGroup("model");
  }

  async function pasteItems() {
    const items = await getClipboardItems();
    pasteClipboardItems(items);
  }

  const pasteableItems = ref<{ type: string }[]>([]);
  function getPasteableItems() {
    pasteableItems.value = [];
    getClipboardItems().then((items) => {
      pasteableItems.value = items;
    });
    return pasteableItems;
  }

  /**
   * Check if the item exists
   */
  function doesItemExist(type: ItemType, id: number | null | undefined) {
    if (type == null) return false;
    if (id == null) return false;
    return modelStore.getItem(type, id) != null;
  }

  function getItem<T extends ItemType>(
    selection?: { type: T; id: number } | null
  ) {
    if (selection == null) return null;
    return modelStore.getItem(selection.type, selection.id);
  }

  /**
   * Resets the ID if the item is invalid.
   * Used for invalidating optional item connections.
   *
   * @example
   *  const item: {
   *    activatorId: 1,
   *    activatorType: "incident"
   *  }
   *  // if the incident with id 1 does not exist, the activatorId will be set to null
   *  resetIfInvalidItem(item, "activatorType", "activatorId");
   */
  function resetIfInvalidItem<TP extends string, IP extends string>(
    item: {
      [k in TP]?: ItemType | null;
    } & { [k in IP]?: number | null },
    typeProp: TP,
    idProp: IP
  ) {
    const id = item[idProp];
    const type = item[typeProp];
    const _item = item as { [k in IP]: number | null };
    if (id != null) {
      if (type != null) {
        const activatorItem = modelStore.getItem(type, id);
        if (activatorItem == null) {
          _item[idProp] = null;
        }
      } else {
        _item[idProp] = null;
      }
    }
  }

  return {
    copyItem,
    copyItems,
    pasteItems,
    pasteableItems,
    getPasteableItems,
    registerPasteEvent
  };
}
